<template>

  <div class="page">
    <!--Title/Actions-->
    <div class="page-header flex-shrink-0 bg-white border-bottom mb-3">
      <div class="container-fluid d-flex justify-content-between align-items-center px-md-4 px-3">
        <h3 class="mb-0"><i class="fas fa-user"></i>&nbsp;Roles</h3>
        <div>
          <b-btn @click="showNewPositionModal" class="btn btn-success"><i class="fas fa-plus"></i> Role</b-btn>
        </div>
      </div>
    </div>

    <!--Search-->
    <div class="container-fluid px-md-4 px-3 py-3 bg-white border-bottom mb-3">
      <div class="row no-gutters">
        <div class="col-lg-4 col-md-6">
          <b-input-group>
            <b-form-input v-model="filter" type="search" id="filterInput" placeholder="Type to Search"></b-form-input>
          </b-input-group>
        </div>
        <b-button :disabled="!filter" variant="link" @click="filter = ''">Clear</b-button>
      </div>
    </div>

    <!--Table-->
    <div class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3" v-if="positionTypes">
      <div>
        <b-table id="position-types-table" bordered class="mb-5 rg-table" :items="positionTypeProvider"
          :fields="positionTypeTableData.fields" no-local-filtering :filter="filter" no-local-sorting
          :sortDesc.sync="sortDesc" :busy.sync="isBusy">
          <template v-slot:cell(name)="data">
            <div class="emphasis">
              {{ data.item.name }}
            </div>
          </template>
          <template v-slot:cell(status)="data">
            <div class="emphasis">
              {{ data.item.active ? "Active" : "Inactive" }}
            </div>
          </template>
          <template v-slot:cell(id)="data">
            <b-btn title="Edit Position Type" @click="showEditPositionModal(data.item.id)" variant="link"><i
                class="fas fa-edit"></i></b-btn>
          </template>
        </b-table>
      </div>
    </div>
    <!--Modal-->
    <b-modal title="Role/Title" v-model="modalOpen" no-close-on-backdrop no-close-on-esc hide-header-close scrollable>
      <div v-if="positionTypeForEdit">
        <b-form>
          <div class="field mb-3">
            <label>Name</label>
            <b-form-input placeholder="Name" v-model="positionTypeForEdit.name"></b-form-input>
          </div>
          <b-form-checkbox v-model="positionTypeForEdit.active">Active Role</b-form-checkbox>
          <b-form-checkbox v-model="positionTypeForEdit.isPocForEvents">Show Role as POC for Events</b-form-checkbox>
          <b-form-checkbox v-model="positionTypeForEdit.seesAllCompanyItems">Sees all company items, regardless of
            assignments *Use carefully</b-form-checkbox>
          <b-form-row>
            <div class="col-12">
              <label class="section-label mt-5 mb-0"><i class="fas fa-check-square"></i> Permissions</label>
              <small class="d-block mb-2">Select permissions the user will have.</small>
            </div>
          </b-form-row>
          <b-form-row v-if="positionTypeForEdit.permissions">
            <div class="col">
              <b-form-checkbox v-model="positionTypeForEdit.permissions.reportEvents">Report Events</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.completeEvents">Complete
                Events</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageCompanyProfile">Manage Company
                Profile</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageInsurancePolicies">Manage Insurance Policies (Beta)</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageInsuranceClaims">Manage Insurance Claims</b-form-checkbox>
              <b-form-checkbox v-if="!hiddenPermissionKeys.has('manageNotificationTemplates')" v-model="positionTypeForEdit.permissions.manageNotificationTemplates">Manage Notification Templates</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageUsers">Manage Users</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageProperties">Manage
                Properties</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.managePropertyGroups">Manage
                PropertyGroups</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageProcedures">Manage
                Procedures</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageEvents">Manage Events</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.deleteEvents">Delete Events</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.viewCorrespondence">View Correspondence</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageCorrespondence">Manage Correspondence</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageVendorTypes">Manage Vendor
                Types</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageVendors">Manage Vendors</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageEventCategories">Manage Event
                Categories</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageRoles">Manage Roles</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.manageSubscriptions">Manage
                Subscriptions</b-form-checkbox>
              <b-form-checkbox v-if="!hiddenPermissionKeys.has('manageImportExport')" v-model="positionTypeForEdit.permissions.manageImportExport">Manage Import &amp; Export</b-form-checkbox>
              <b-form-checkbox v-if="!hiddenPermissionKeys.has('exportCompanyArchive')" v-model="positionTypeForEdit.permissions.exportCompanyArchive">Export Company Archive</b-form-checkbox>
              <b-form-checkbox v-if="!hiddenPermissionKeys.has('viewQRCode')" v-model="positionTypeForEdit.permissions.viewQRCode">View QR Code &amp; File Sharing</b-form-checkbox>
              <b-form-checkbox v-if="!hiddenPermissionKeys.has('useAiChat')" v-model="positionTypeForEdit.permissions.useAiChat">Use AI Events Chat Assistant</b-form-checkbox>
              <b-form-checkbox v-model="positionTypeForEdit.permissions.viewDashboard">View Dashboard</b-form-checkbox>
              <b-form-checkbox v-if="isRgAdmin && !hiddenPermissionKeys.has('manageCompanies')" v-model="positionTypeForEdit.permissions.manageCompanies">Manage Companies</b-form-checkbox>
              
            </div>
          </b-form-row>
        </b-form>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="toggleModal">Close</b-button>
        <b-button variant="primary" @click="SavePositionType">Save</b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import { isResponseGridAdmin } from '@/auth/responseGridAdmin'

export default {
  name: 'PositionTypes',
  created () {
    this.fields = [
      { key: 'name', label: 'Role/Title', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'id', label: 'Actions' }]
  },
  components: {
  },
  data () {
    return {
      modalOpen: false,
      fields: [],
      isBusy: false,
      sortBy: 'name',
      sortDirection: 'asc',
      filter: null,
      sortDesc: true,
      positionTypes: [],
      positionTypeForEdit: null
    }
  },
  computed: {
    isRgAdmin () {
      const user = this.$auth && this.$auth.user
      return isResponseGridAdmin(user && user.email)
    },
    /**
     * Returns a Set of permissionKey strings whose flags are currently disabled
     * AND have hidePermissionWhenDisabled = true. Used to conditionally hide
     * permission checkboxes for brand-new (never-on-production) features.
     * For the initial 4 subscription flags, hidePermissionWhenDisabled = false
     * so this set will be empty until a new flag is added with that option set.
     */
    hiddenPermissionKeys () {
      return this.$store.getters['featureFlags/hiddenPermissionKeys']
    },
    positionTypeTableData () {
      var returnPositionTypes = {
        fields: this.fields
      }
      console.log(
        'Position Type Table Data is: ' +
        JSON.stringify(returnPositionTypes, null, 2)
      )
      return returnPositionTypes
    }
  },
  methods: {
    ...mapActions('company', ['getPositionTypes', 'savePositionType']),
    positionTypeProvider (ctx) {
      this.isBusy = true
      console.log(
        'position type table ctx is: ' +
        JSON.stringify(ctx, null, 2)
      )
      var request = { sortBy: ctx.sortBy, sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter }

      // Must return a promise that resolves to an array of items
      return this.getPositionTypes(request).then(r => {
        // Pluck the array of items off our axios response
        const items = r.data.types
        console.log(
          'Returned Position Type Table Data is: ' +
          JSON.stringify(items, null, 2)
        )
        this.isBusy = false
        this.positionTypes = items
        // Must return an array of items or an empty array if an error occurred
        return items || []
      })
    },
    toggleModal () {
      this.modalOpen = !this.modalOpen
    },
    async SavePositionType (data) {
      const newPositionType = this.positionTypeForEdit
      this.savePositionType(newPositionType)
        .then(() => {
          this.$root.$emit('bv::refresh::table', 'position-types-table')
          this.toggleModal()
          this.$toasted.show('Position Saved.', {duration: 4000})
          this.$auth.refreshCompanyPermissionsForUser()
        })
    },
    showEditPositionModal (id) {
      this.positionTypeForEdit = this.positionTypes.find(p => p.id === id)
      this.toggleModal()
    },
    showNewPositionModal () {
      this.positionTypeForEdit = {name: 'Position', active: true, permissions: { reportEvents: false, manageCompanyProfile: false, manageProperties: false, managePropertyGroups: false, manageProcedures: false, manageEvents: false, manageVendorTypes: false, manageVendors: false, manageEventCategories: false, mangeRoles: false, manageUsers: false, manageInsurancePolicies: false, manageInsuranceClaims: false, manageNotificationTemplates: false, manageImportExport: false, exportCompanyArchive: false, viewQRCode: false, useAiChat: false, viewDashboard: false, deleteEvents: false, manageSubscriptions: false, viewCorrespondence: false, manageCorrespondence: false }, isPocForEvents: true, seesAllCompanyItems: false}

      this.toggleModal()
    }
  }
}
</script>

<style>
  .ui.modals.active .modal {
    height: auto;
  }
</style> 